import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, BarChart3, Sparkles, FileText, Users, Send, Settings,
  Menu, LogOut, Briefcase, Building2, LayoutTemplate, FolderLock, Plug, Inbox, FileSignature, HeartHandshake, CreditCard,
  Rocket, PieChart, Vault, HandHelping, UsersRound, Globe2, Grid3x3, Presentation, ListChecks, Gauge, Landmark, ClipboardCheck, Sparkles as SparklesIcon,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { PlanBadge } from "@/components/PlanBadge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { needsUpgrade, openUpgrade } from "@/lib/planFeatures";
import { CompanySwitcher } from "@/components/app/CompanySwitcher";

// Persist the sidebar's scroll position across route changes (each page renders its own
// AppShell, so the nav would otherwise reset to the top on every navigation).
let navScrollTop = 0;

const FOUNDER_NAV = [
  { section: "Report" },
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/app/metrics", label: "Metrics", icon: BarChart3 },
  { to: "/app/structure", label: "AI Structure Notes", icon: Sparkles },
  { to: "/app/updates", label: "Updates", icon: FileText },
  { to: "/app/templates", label: "Templates", icon: LayoutTemplate },
  { section: "Investors" },
  { to: "/app/investors", label: "Investors", icon: Users },
  { to: "/app/investor-impact", label: "Investor Impact", icon: Gauge },
  { to: "/app/fundraising", label: "Fundraising", icon: Rocket, minPlan: "growth" },
  { to: "/app/directory", label: "Investor Directory", icon: Globe2 },
  { to: "/app/asks", label: "Asks & Q&A", icon: HandHelping },
  { to: "/app/delivery", label: "Delivery & Engagement", icon: Send },
  { section: "Equity & Docs" },
  { to: "/app/cap-table", label: "Cap Table", icon: Grid3x3, minPlan: "growth" },
  { to: "/app/rounds", label: "Rounds & Ownership", icon: PieChart, minPlan: "growth" },
  { to: "/app/funds", label: "Funds & SPVs", icon: Landmark, minPlan: "scale" },
  { to: "/app/sweat-equity", label: "Sweat Equity", icon: HeartHandshake },
  { to: "/app/documents", label: "Documents", icon: FolderLock },
  { to: "/app/document-builder", label: "Document Builder", icon: FileSignature, minPlan: "scale" },
  { to: "/app/signatures", label: "Signatures", icon: ListChecks, minPlan: "scale" },
  { to: "/app/deck-links", label: "Deck Links", icon: Presentation, minPlan: "growth" },
  { to: "/app/data-rooms", label: "Data Rooms", icon: Vault, minPlan: "scale" },
  { to: "/app/requests", label: "Requests", icon: Inbox },
  { section: "Diligence & AI" },
  { to: "/app/diligence", label: "Diligence Room", icon: ClipboardCheck, minPlan: "growth" },
  { to: "/app/advisor", label: "Ask TenX", icon: SparklesIcon, minPlan: "growth" },
  { section: "Workspace" },
  { to: "/app/integrations", label: "Integrations", icon: Plug },
  { to: "/app/team", label: "Team", icon: UsersRound },
  { to: "/app/billing", label: "Billing & Plan", icon: CreditCard },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

const INVESTOR_NAV = [
  { to: "/portal", label: "Portfolio", icon: Briefcase, end: true },
  { to: "/portal/funds", label: "Fund Positions", icon: Landmark },
  { to: "/portal/impact", label: "Your Impact", icon: Gauge },
  { to: "/portal/account", label: "Account", icon: Settings },
];

const NavItems = ({ items, onNavigate, plan, variant }) => (
  <nav className="flex flex-col gap-0.5">
    {items.map((item, idx) => {
      if (item.section) {
        return <div key={`s-${idx}`} className="ff-mono mt-4 px-3 pb-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-white/60 first:mt-0">{item.section}</div>;
      }
      const locked = plan != null && needsUpgrade(plan, item.minPlan);
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onNavigate?.(); if (locked) openUpgrade({ plan: item.minPlan }); }}
          data-testid={`${variant === "mobile" ? "mobile-" : ""}nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-brand/15 text-white" : "text-white/65 hover:bg-white/[0.06] hover:text-white"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {locked && <PlanBadge plan={item.minPlan} testId={`nav-plan-${variant}-${item.to.split("/").pop()}`} />}
        </NavLink>
      );
    })}
  </nav>
);

// Sidebar body - defined at module scope (stable identity) so React never remounts the
// scrollable nav on re-render; that plus the callback ref below keeps its scroll position.
const SidebarInner = ({ isFounder, company, user, items, onNavigate, scrollRef, onScroll, onUserScroll, doLogout, variant = "desktop" }) => (
  <div className="flex h-full flex-col">
    <div className="px-4 py-5"><Logo light testId={variant === "mobile" ? "app-mobile-logo" : "app-logo"} /></div>
    <div className="px-3">
      {isFounder ? <CompanySwitcher variant={variant} /> : <div className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2">
        <Building2 className="h-4 w-4 text-brand" />
        <div className="min-w-0">
          <div data-testid={`${variant}-workspace-name`} className="truncate text-sm font-semibold text-white">
            {isFounder ? (company?.name || "Your Company") : "Investor Portal"}
          </div>
          <div className="ff-mono truncate text-[9px] uppercase tracking-[0.14em] text-white/60">{user?.role} workspace</div>
        </div>
      </div>}
    </div>
    <div ref={scrollRef} onScroll={onScroll} onWheel={onUserScroll} onTouchMove={onUserScroll} onKeyDown={onUserScroll} data-testid={variant === "mobile" ? "mobile-sidebar-scroll" : "sidebar-scroll"} className="flex-1 overflow-y-auto px-3 py-2"><NavItems items={items} onNavigate={onNavigate} variant={variant} plan={isFounder ? (company?.plan || "starter") : null} /></div>
    <div className="border-t border-white/10 p-3">
      <div className="mb-2 px-1 text-xs">
        <div data-testid={`${variant}-user-name`} className="flex items-center gap-1.5 font-medium text-white">
          <span className="truncate">{user?.name}</span>
        </div>
        <div data-testid={`${variant}-user-email`} className="truncate text-white/65">{user?.email}</div>
      </div>
      <Button variant="outline" className="w-full justify-start gap-2 border-white/30 bg-transparent text-white/85 hover:bg-white/[0.06] hover:text-white" onClick={doLogout} data-testid={variant === "mobile" ? "mobile-logout-button" : "logout-button"}>
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  </div>
);

export const AppShell = ({ children, title, subtitle, actions, headerContext }) => {
  useEffect(() => { sessionStorage.removeItem("tenx_post_auth_redirect"); }, []);
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isFounder = user?.role === "founder";
  const items = isFounder ? FOUNDER_NAV : INVESTOR_NAV;

  const doLogout = () => { logout(); navigate("/login"); };

  // Keep the sidebar's scroll position stable across navigations. The nav is only saved on a
  // genuine user gesture (wheel/touch/pointer/keyboard) so programmatic "scroll target into view"
  // side-effects don't corrupt it; after each navigation we restore the last user position.
  const location = useLocation();
  const navElRef = useRef(null);
  const userScrollUntil = useRef(0);
  const markUserScroll = () => { userScrollUntil.current = Date.now() + 250; };
  const setNavRef = useCallback((el) => { navElRef.current = el; if (el) el.scrollTop = navScrollTop; }, []);
  useLayoutEffect(() => {
    const el = navElRef.current;
    if (!el) return;
    el.scrollTop = navScrollTop;
    const raf = requestAnimationFrame(() => { if (navElRef.current) navElRef.current.scrollTop = navScrollTop; });
    const t = setTimeout(() => { if (navElRef.current) navElRef.current.scrollTop = navScrollTop; }, 160);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [location.pathname]);
  const saveNavScroll = (e) => { if (Date.now() < userScrollUntil.current) navScrollTop = e.currentTarget.scrollTop; };

  return (
    <div className="flex min-h-screen bg-background">
      <aside data-testid="app-sidebar" className="tenx-sidebar hidden w-[264px] shrink-0 lg:block">
        <div className="sticky top-0 h-screen"><SidebarInner isFounder={isFounder} company={company} user={user} items={items} doLogout={doLogout} scrollRef={setNavRef} onScroll={saveNavScroll} onUserScroll={markUserScroll} onNavigate={() => { if (navElRef.current) navScrollTop = navElRef.current.scrollTop; }} /></div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open workspace navigation" data-testid="mobile-nav-trigger">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="mobile-sidebar" className="tenx-sidebar w-[280px] border-white/10 bg-sidebar p-0 text-white [&>button]:text-white">
                <SheetTitle className="sr-only" data-testid="mobile-sidebar-title">Workspace navigation</SheetTitle>
                <SheetDescription className="sr-only" data-testid="mobile-sidebar-description">Navigate your workspace, switch companies, or sign out.</SheetDescription>
                <SidebarInner variant="mobile" isFounder={isFounder} company={company} user={user} items={items} doLogout={doLogout} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="min-w-0 flex-1">
              <h1 data-testid="app-page-title" className="h-display truncate text-lg font-bold tracking-tight text-foreground">{title}</h1>
              {subtitle && <p data-testid="app-page-subtitle" className="truncate text-xs text-muted-foreground">{subtitle}</p>}
            </div>
            {headerContext}
            <NotificationBell />
            {actions && <div data-testid="app-page-actions" className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto [&>*]:max-w-full [&>*]:flex-wrap">{actions}</div>}
          </div>
        </header>

        <motion.main
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
          className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 sm:px-6 lg:px-8"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
};
